import { makeStyles } from '@material-ui/styles';
import { Radio } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { orderStage, bookingJourneyType } from 'common/constants/booking';
import SelectFilter from 'common/components/select-filter';
import { orderAll } from './useTableFilters';

const useStyles = makeStyles(
  theme => ({
    filter: {
      ...theme.flex.mainStart,
      marginBottom: '16px',

      '& > *:not(:first-child)': { marginLeft: 8 },
    },
    radioBtn: {
      ...theme.flex.mainCenter,
    },
    radioBtnCount: {
      height: 16,
      minWidth: 16,
      marginTop: 7,
      marginLeft: 4,
      textAlign: 'center',
      fontSize: '10px',
      lineHeight: '18px',
      color: theme.colors.gray1,
      borderRadius: theme.borderRadius.full,
      backgroundColor: theme.colors.red6,
    },
  }),
  { name: 'BookingStageAndPropertyNameFilters' },
);

const BookingStageAndPropertyNameFilters = ({ bookingType, bookingStage, setBookingStage }) => {
  const { t } = useTranslation(['landlord', 'cms']);
  const classes = useStyles();

  const orderStagesRadioGroup = useMemo(
    () => [
      { value: orderAll, label: t('cms:cms.booking.message_property.table.booking_status.all') },
      {
        value: orderStage.WAITING_FOR_CONFIRMATION,
        label: t(
          `cms:cms.booking.message_property.table.booking_status.${orderStage.WAITING_FOR_CONFIRMATION.toLocaleLowerCase()}`,
        ),
      },
      {
        value: orderStage.REFUNDING,
        label: t(
          `cms:cms.booking.message_property.table.booking_status.${orderStage.REFUNDING.toLocaleLowerCase()}`,
        ),
      },
    ],
    [t],
  );

  const handleClickRadioGroup = e => {
    setBookingStage(e?.target?.value);
  };

  const statusOptions = useMemo(
    () =>
      [
        orderStage.CHECKED_IN,
        orderStage.LOST,
        orderStage.CANCELLED,
        orderStage.COMPLETED,
        orderStage.WAITING_FOR_CHECK_IN,
        orderStage.WAITING_FOR_PAYMENT,
      ].map(item => ({
        value: item,
        label: t(
          `cms:cms.booking.message_property.table.booking_status.${item?.toLocaleLowerCase()}`,
        ),
      })),
    [],
  );

  const btnText = useMemo(() => {
    return !bookingStage ||
      [orderAll, orderStage.WAITING_FOR_CONFIRMATION, orderStage.REFUNDING].includes(bookingStage)
      ? t('landlord:booking_status')
      : t(
          `cms:cms.booking.message_property.table.booking_status.${bookingStage.toLocaleLowerCase()}`,
        );
  }, [bookingStage, bookingType, t]);

  return (
    <div className={classes.filter}>
      <Radio.Group value={bookingStage ?? orderAll} onChange={handleClickRadioGroup}>
        <For of={orderStagesRadioGroup} each="item">
          <Radio.Button value={item?.value} key={item?.value}>
            <div className={classes.radioBtn}>
              <span>{item.label}</span>
            </div>
          </Radio.Button>
        </For>
      </Radio.Group>

      <If condition={bookingType === bookingJourneyType.SEMI_AUTOMATIC}>
        <SelectFilter
          value={bookingStage ?? orderAll}
          options={statusOptions}
          btnText={btnText}
          filterIcon={<FilterOutlined />}
          onSelect={setBookingStage}
        />
      </If>
    </div>
  );
};

BookingStageAndPropertyNameFilters.propTypes = {
  bookingStage: PropTypes.string,
  bookingType: PropTypes.string,
  setBookingStage: PropTypes.func,
  pendingConfirmationOrderCount: PropTypes.number,
  pendingRefundOrderCount: PropTypes.number,
};

export default BookingStageAndPropertyNameFilters;
